import { HubConnectionBuilder, HttpTransportType } from '@microsoft/signalr';
import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { emitProfileInductionStatusChanged } from '../utils/operation-feedback-events';

const PROFILE_UPDATE_EVENT = 'ProfileUpdateOperationStatusChanged';
const PROFILE_INDUCTION_EVENT = 'ProfileInductionOperationStatusChanged';

// Long Polling only: accessTokenFactory sends the JWT in the Authorization header.
// WebSockets would force ?access_token= in the URL (browser WS API cannot set headers).
export default function OperationFeedbackListener() {
  const auth = useAuth();
  const { isLoading, user } = auth;
  const currentUserId = user?.profile?.sub;
  const accountId = useSelector((state) => state.app?.account?.id);
  const { enqueueSnackbar } = useSnackbar();
  const isEnabled = process.env.REACT_APP_OPERATION_FEEDBACK_HUB_ENABLED === 'true';
  const hubUrl = process.env.REACT_APP_OPERATION_FEEDBACK_HUB_URL;

  // Keep latest auth/UI callbacks in refs so token refresh does not tear down the hub connection.
  const authRef = useRef(auth);
  authRef.current = auth;
  const currentUserIdRef = useRef(currentUserId);
  currentUserIdRef.current = currentUserId;
  const enqueueSnackbarRef = useRef(enqueueSnackbar);
  enqueueSnackbarRef.current = enqueueSnackbar;

  useEffect(() => {
    if (!isEnabled || !hubUrl || !accountId || isLoading) {
      return undefined;
    }
    let cancelled = false;
    let connection;

    const getAccessToken = async () => {
      const { user: currentUser, signinSilent } = authRef.current;
      if (currentUser?.access_token && !currentUser.expired) {
        return currentUser.access_token;
      }

      try {
        const refreshedUser = await signinSilent();
        return refreshedUser?.access_token ?? '';
      } catch (error) {
        console.error('[OperationFeedbackHub] Token refresh error', error);
        return '';
      }
    };

    const startConnection = async () => {
      const initialToken = await getAccessToken();
      if (!initialToken) {
        console.warn('[OperationFeedbackHub] Skipping connection: missing access token');
        return;
      }

      connection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: getAccessToken,
          transport: HttpTransportType.LongPolling,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .build();

      connection.on(PROFILE_UPDATE_EVENT, (payload) => {
        console.log('[OperationFeedbackHub] Profile update status', payload);
      });
      connection.on(PROFILE_INDUCTION_EVENT, (payload) => {
        console.log('[OperationFeedbackHub] Profile induction status', payload);

        // Every connected user on the account receives this event so everyone's data stays
        // in sync, but only the user who actually triggered the induction/retry should see a
        // personal toast about it.
        const isOwnAction =
          Boolean(payload?.userId) && payload.userId === currentUserIdRef.current;

        if (isOwnAction) {
          const isFailure = payload?.status === 'Failed';
          const profileName = [payload?.firstName, payload?.lastName]
            .filter(Boolean)
            .join(' ')
            .trim();
          const statusMessage = isFailure
            ? 'Profile induction failed'
            : '- profile induction completed successfully';
          const toastMessage = profileName
            ? `${profileName} ${statusMessage}`
            : statusMessage;

          enqueueSnackbarRef.current(toastMessage, {
            variant: isFailure ? 'error' : 'success',
          });
        }

        emitProfileInductionStatusChanged(payload);
      });

      connection.onreconnected(async () => {
        try {
          await connection.invoke('SubscribeForAccount', accountId);
          console.log('[OperationFeedbackHub] Reconnected and re-subscribed');
        } catch (error) {
          console.error('[OperationFeedbackHub] Re-subscribe error', error);
        }
      });

      connection.onclose((error) => {
        if (error) {
          console.error('[OperationFeedbackHub] Connection closed with error', error);
        }
      });

      try {
        await connection.start();
        if (cancelled) {
          return;
        }

        console.log('[OperationFeedbackHub] Connected');
        await connection.invoke('SubscribeForAccount', accountId);
      } catch (error) {
        console.error('[OperationFeedbackHub] Connection error', error);
      }
    };

    startConnection();

    return () => {
      cancelled = true;
      if (!connection) {
        return;
      }

      connection.off(PROFILE_UPDATE_EVENT);
      connection.off(PROFILE_INDUCTION_EVENT);
      connection.stop().catch((error) => {
        console.error('[OperationFeedbackHub] Stop error', error);
      });
    };
  }, [isEnabled, hubUrl, isLoading, accountId]);

  return null;
}
